import { takeEvery, all, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { SweetAlertResult } from 'sweetalert2';
import apiService from 'app/bff/services/apiService';
import { ReturnCodesEnum } from 'app/bff/enums/codes';
import { PaymentMethodsEnum } from 'app/bff/enums/payment';
import { BaseResp } from 'app/bff/models/base';
import { PolicyPodetailResp } from 'app/bff/models/policy/policyPodetail';
import { AaOtpSendcodeResp } from 'app/bff/models/aa/aaOtpSendcode';
import { AaOtpVerifyResp } from 'app/bff/models/aa/aaOtpVerify';
import { PolicyAmendTravelperiodResp } from 'app/bff/models/policy/policyAmendTravelperiod';
import { PaymentCreditAuthResp } from 'app/bff/models/payment/paymentCreditAuth';
import { ROUTES } from 'app/core/router/paths';
import modalService from 'app/core/services/modalService';
import { ProcessRouteMatchesStep } from 'app/features/Service/QueryPolicy/Modify/types';
import { RootState } from 'app/store/types';
import { sagaBoundary } from 'app/store/service';
import { ModifyState, FetchPolicyDetailAction, SERVICE_QUERY_POLICY_MODIFY__FETCH_POLICY_DETAIL, SERVICE_QUERY_POLICY_MODIFY__SEND_OTP, SERVICE_QUERY_POLICY_MODIFY__RESEND_OTP, SERVICE_QUERY_POLICY_MODIFY__AUTH_OTP, SERVICE_QUERY_POLICY_MODIFY__MODIFY_POLICY_PERIOD, SERVICE_QUERY_POLICY_MODIFY__EXECUTE_CREDIT_CARD_PAYMENT, SendOTPAction, ResendOTPAction, AuthOTPAction, ModifyPolicyPeriodAction, ExecuteCreditCardPaymentAction } from './types';
import { fetchPolicyDetailDoneAction, setAccessibleStepAction, sendOTPDoneAction, resendOTPDoneAction, modifyPolicyPeriodAction, saveModifyAction } from './actions';

/**
 * @description 取得保單明細
 */
function * fetchPolicyDetail (action: FetchPolicyDetailAction) {
  const response: PolicyPodetailResp['policyTravel'] = yield call(apiService.postPolicyPodetail, action.payload.args);
  if (response) {
    // 取得保單明細 完成
    yield put(fetchPolicyDetailDoneAction(response));
    // 導頁至保單查詢我要繳費流程 (透過路由帶入資料)
    const state = { detail: response };
    yield put(push(ROUTES.SERVICE__QUERY_POLICY__MODIFY__POLICY_INFO, state));
  }
}

/**
 * @description 發送 OTP 驗證碼
 */
function * sendOTP (action: SendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 緩存 OTP 資料
    yield put(sendOTPDoneAction(response));
    // 將「確認資料」(Step-2-2) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.SERVICE__QUERY_POLICY__MODIFY__CONFIRM_INFO__OTP_AUTH]));
    // 導頁至「確認資料」(Step-2-2)
    yield put(push(ROUTES.SERVICE__QUERY_POLICY__MODIFY__CONFIRM_INFO__OTP_AUTH));
  }
}

/**
 * @description 重新發送 OTP 驗證碼
 */
function * resendOTP (action: ResendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 更新 OTP 資料
    yield put(resendOTPDoneAction(response));
  }
}

/**
 * @description 驗證 OTP 驗證碼
 */
function * authOTP (action: AuthOTPAction) {
  // 說明：
  // 若驗證 OTP 成功，判斷是否需繳費。
  // => 需繳費：進入繳費流程，繳費成功後呼叫「旅遊險保期修改 API」(IR#017)。
  // => 不需繳費：呼叫「旅遊險保期修改 API」(IR#017)。
  const response: AaOtpVerifyResp = yield call(apiService.postAaOtpVerify, action.payload.authOTPArgs);
  if (response) {
    const modifyState: ModifyState = yield select((state: RootState) => state.features.service.queryPolicy.modify);
    const detailState = modifyState.static.detail;
    const policyInfoPageState = modifyState.process.policyInfo;
    if (detailState && policyInfoPageState.data) {
      const shouldPaid = policyInfoPageState.data.totalPremium > detailState.premTotal; // 是否需要繳費 (條件：改期後新保費 > 原保單保費 = 需要繳費)。
      if (shouldPaid) {
        // 需要繳費，進入繳費流程
        switch (detailState.payBy) {
          // 帳戶轉帳
          case PaymentMethodsEnum.Transfer: {
            // 將「線上繳費/帳戶轉帳」(Step-3) 設為可訪問步驟
            yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.SERVICE__QUERY_POLICY__MODIFY__PAYMENT__TRANSFER]));
            // 導頁至「線上繳費/帳戶轉帳」(Step-3)
            const state = { modifyPolicyPeriodArgs: action.payload.modifyPolicyPeriodArgs };
            // 另存至 redux，避免原頁跳轉後再回繳費頁時資料消失
            yield put(saveModifyAction(action.payload.modifyPolicyPeriodArgs));
            yield put(push(ROUTES.SERVICE__QUERY_POLICY__MODIFY__PAYMENT__TRANSFER, state));
            break;
          }
          // 信用卡
          case PaymentMethodsEnum.CreditCard: {
            // 將「線上繳費/信用卡」(Step-3) 設為可訪問步驟
            yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.SERVICE__QUERY_POLICY__MODIFY__PAYMENT__CREDIT_CARD]));
            // 導頁至「線上繳費/信用卡」(Step-3)
            const state = { modifyPolicyPeriodArgs: action.payload.modifyPolicyPeriodArgs };
            // 另存至 redux，避免原頁跳轉後再回繳費頁時資料消失
            yield put(saveModifyAction(action.payload.modifyPolicyPeriodArgs));
            yield put(push(ROUTES.SERVICE__QUERY_POLICY__MODIFY__PAYMENT__CREDIT_CARD, state));
            break;
          }
        }
      } else {
        // 不需要繳費，執行保期修改
        // 執行保期修改
        yield put(modifyPolicyPeriodAction(action.payload.modifyPolicyPeriodArgs));
      }
    }
  }
}

/**
 * @description 執行信用卡繳費
 */
function * executeCreditCardPayment (action: ExecuteCreditCardPaymentAction) {
  const response: BaseResp<PaymentCreditAuthResp> = yield call(apiService.postPaymentCreditAuth, action.payload.args);
  if (response.respHeader.returnCode === ReturnCodesEnum.CodeSuccess) {
    // 執行保期修改
    yield put(modifyPolicyPeriodAction(action.payload.modifyPolicyPeriodArgs));
  } else {
    const result: SweetAlertResult = yield modalService.base('繳費失敗', '如欲修改保期，請重新操作', '回網路保單查詢');
    if (result) yield put(push(ROUTES.SERVICE__QUERY_POLICY__LIST));
  }
}

/**
 * @description 執行保期修改
 */
function * modifyPolicyPeriod (action: ModifyPolicyPeriodAction) {
  const response: PolicyAmendTravelperiodResp = yield call(apiService.postPolicyAmendTravelperiod, action.payload.args);
  if (response) {
    // 將「修改完成」頁步驟 (Step-4) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.SERVICE__QUERY_POLICY__MODIFY__RESULT]));
    // 導頁至「修改完成」頁 (Step-4)
    yield put(push(ROUTES.SERVICE__QUERY_POLICY__MODIFY__RESULT));
  }
}

export default function * watchModifySaga () {
  yield all([
    takeEvery(SERVICE_QUERY_POLICY_MODIFY__FETCH_POLICY_DETAIL, sagaBoundary(fetchPolicyDetail)),
    takeEvery(SERVICE_QUERY_POLICY_MODIFY__SEND_OTP, sagaBoundary(sendOTP)),
    takeEvery(SERVICE_QUERY_POLICY_MODIFY__RESEND_OTP, sagaBoundary(resendOTP)),
    takeEvery(SERVICE_QUERY_POLICY_MODIFY__AUTH_OTP, sagaBoundary(authOTP)),
    takeEvery(SERVICE_QUERY_POLICY_MODIFY__EXECUTE_CREDIT_CARD_PAYMENT, sagaBoundary(executeCreditCardPayment)),
    takeEvery(SERVICE_QUERY_POLICY_MODIFY__MODIFY_POLICY_PERIOD, sagaBoundary(modifyPolicyPeriod))
  ]);
}
